import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import {
    IconDefinition,
    faArrowsRotate,
    faCircleDollar,
    faHeart,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchStats } from 'api/transactionPlans';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twMerge } from 'tailwind-merge';

const RecurringPaymentsMetrics = () => {
    const { accountId, isRecurringPaymentsEnabled } = useCurrentAccount();

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.plansStats(accountId),
        queryFn: () =>
            conditionallyFetchOrNull(() => fetchStats(accountId), isRecurringPaymentsEnabled),
    });

    return (
        <div className={'grid grid-cols-12 gap-4'}>
            <MetricColumn label={'Total Active ($)'} stat={`$${data?.total}`} icon={faHeart} />
            <MetricColumn label={'Active Plans'} stat={data?.count} icon={faArrowsRotate} />
            <MetricColumn label={'Average Plan'} stat={`$${data?.average}`} icon={faCircleDollar} />
        </div>
    );
};

interface MetricColumnProps extends ComponentPropsWithoutRef<'div'> {
    label?: ReactNode;
    stat?: ReactNode;
    icon?: IconDefinition;
}

const MetricColumn = (props: MetricColumnProps) => {
    const { label, stat, icon = faHeart, className = '', ...rest } = props;
    return (
        <Box
            className={twMerge(
                `col-span-12 flex gap-x-4 p-4 sm:col-span-6 md:col-span-4`,
                className,
            )}
            {...rest}
        >
            <BoxIcon size="lg">
                <FontAwesomeIcon icon={icon} />
            </BoxIcon>

            <div className={'flex flex-col'}>
                <p className={'text-sm font-medium text-gray-600'}>{label}</p>
                <p className={'text-2xl font-medium text-gray-900'}>{stat}</p>
            </div>
        </Box>
    );
};

export { RecurringPaymentsMetrics };
